import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, DumbbellIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, TimerIcon, TrophyIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
// КОШАРКА СПРЕМНА™ – Serbian (ekavian, Cyrillic) copy for youth basketball coaches. Source of truth: kosarka-spremna/PRODUCTO.md.
export const BRAND = 'КОШАРКА СПРЕМНА™';
export const heroGuarantees = ['Тренутни дигитални приступ', 'Једнократна уплата', 'Без претплате', '7 дана гаранције задовољства'];
export const stats = [{
  value: '6',
  label: 'Дигиталних томова'
}, {
  value: '240',
  label: 'Страна у шест томова'
}, {
  value: '150+',
  label: 'Вежби и игара са дијаграмима терена'
}, {
  value: '5',
  label: 'Бонуса у пакету'
}, {
  value: '3',
  label: 'Нивоа – основни, средњи, напредни'
}];
export const differenceStats = [{
  value: '6–16 година',
  label: 'Школа кошарке, млађи пионири, пионири и кадети'
}, {
  value: '6 дигиталних томова',
  label: 'Од годишњег плана и готових тренинга до технике, тактике, физичке припреме и праћења напретка'
}, {
  value: 'PDF спреман за штампу',
  label: 'Користите материјал на телефону у сали или га одштампајте за свеску тренера'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Тренинг се смишља у колима',
  text: 'Пола сата пре тренинга још размишљате шта ћете данас радити – и опет се враћате на исте три вежбе.'
}, {
  title: 'Нема плана за целу сезону',
  text: 'Знате да деци треба прогресија од септембра до јуна, али немате време да направите годишњи план по узрасту.'
}, {
  title: 'Различити нивои у истој групи',
  text: 'Неко тек учи дриблинг, неко већ шутира за три поена – а тренинг мора да функционише за све.'
}, {
  title: 'Деци брзо досади',
  text: 'Када нема довољно игара и варијанти, пада концентрација, расте галама и губи се време на објашњавање.'
}, {
  title: 'Родитељи и утакмице одузимају енергију',
  text: 'Поруке у Viber групи, минутажа, турнири, понашање на трибинама – све то иде уз сам тренинг.'
}, {
  title: 'Напредак се не види',
  text: 'Без тестова и листова за праћење тешко је показати играчу, родитељу и клубу шта је урађено.'
}];
export const afterItems = ['Годишњи план септембар–јун за 4 узрасне групе', 'Припремни, такмичарски и прелазни период', '12 готових тренинга минут по минут у Тому 1 + 24 у Тому 2', 'Материјал, организација и упутство за тренера уз сваки тренинг', '150+ вежби и игара са дијаграмима терена', 'Свака техника у 3 нивоа – основни, средњи, напредни', 'Физичка припрема и безбедан рад по узрасту', 'Тестови, листови напретка и 100 готових коментара', 'PDF спреман за штампу'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Вечери одлазе на планирање',
  text: 'Да ли после посла проводите вечери смишљајући тренинге за следећу недељу?'
}, {
  icon: FolderSearchIcon,
  title: 'Тражите вежбе по интернету',
  text: 'Да ли стално тражите вежбе и игре које одговарају узрасту ваше групе?'
}, {
  icon: UsersIcon,
  title: 'Мешовита група',
  text: 'Да ли вам требају вежбе које функционишу и за почетнике и за децу која већ добро играју?'
}, {
  icon: TargetIcon,
  title: 'Техника стоји у месту',
  text: 'Видите исте грешке у дриблингу, шуту и раду ногу, а немате јасан редослед корекција?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Нема праћења напретка',
  text: 'Желите готове тестове, листове напретка и коментаре за играче и родитеље?'
}, {
  icon: LayoutGridIcon,
  title: 'Недостаје јасна структура',
  text: 'Желите да све буде на једном месту, да за пет минута нађете прави тренинг пре него што уђете у салу?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Том 01',
  level: 'Сезона',
  title: 'Годишњи план и периодизација + 12 готових тренинга',
  tagline: 'Тачно знате шта радите на сваком тренингу – од септембра до јуна.',
  intro: 'Први том вам даје готов план сезоне за четири узрасне групе и дванаест тренинга које можете одмах да одржите.',
  items: ['Сезона септембар–јун + летњи камп', 'Школа кошарке 6–10, млађи пионири, пионири и кадети', 'Припремни, такмичарски и прелазни период', 'Месечни циљеви и недељни микроциклус', 'Структура тренинга: загревање → техника → игра → завршни део', '12 готових тренинга минут по минут (3 по узрасној групи)', 'Материјал, организација и упутство за тренера', 'Дијаграм терена уз сваки тренинг', 'Табела прогресије и контролна листа пред тренинг'],
  outro: 'Уђите у салу са планом – сваки пут.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 02',
  level: 'Тренинзи',
  title: 'Готови тренинзи минут по минут',
  tagline: '24 тренинга – сваки на једној страни.',
  intro: 'Други том има шест готових тренинга за сваку узрасну групу, спремних за штампу или за телефон у сали.',
  items: ['24 тренинга (6 по узрасној групи)', 'Циљ, трајање и материјал', 'Блокови по минутима', 'Дијаграм терена', 'Варијанта за мању и већу групу', 'Кључне речи за тренера'],
  outro: 'Отворите страну, погледајте дијаграм и почните.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 03',
  level: 'Техника',
  title: 'Техника у 3 нивоа',
  tagline: 'Свака вежба у три нивоа – за целу групу.',
  intro: 'Трећи том покрива основне елементе технике са 60 вежби, честим грешкама и корекцијама.',
  items: ['Дриблинг', 'Додавање: груди, одбијено, преко главе', 'Шут, форма шута и слободно бацање', 'Двокорак (полагање)', 'Рад ногу, пивотирање и заустављање у један/два темпа', 'Скок', '60 вежби: основни, средњи и напредни ниво', 'Дијаграм, честе грешке и корекције'],
  outro: 'Јасан редослед од првог дриблинга до сигурног шута.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 04',
  level: 'Тактика',
  title: 'Тактика и игра + 50 игара за децу',
  tagline: 'Деца уче кошарку кроз игру.',
  intro: 'Четврти том води од 1 на 1 до 5 на 5, са принципима игре у нападу и одбрани и 50 игара по узрасту.',
  items: ['Принципи игре у нападу и одбрани', '1 на 1, 2 на 2, 3 на 3 и 5 на 5', 'Транзиција (контранапад)', 'Одбрана човек на човека (без зоне за најмлађе)', 'Блок и одвајање за кадете', '50 игара за загревање и такмичарски део', 'Правила за мини-баскет'],
  outro: 'Више игре, мање стајања у реду.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 05',
  level: 'Кондиција',
  title: 'Физичка припрема и безбедан рад по узрасту',
  tagline: 'Кретање прилагођено узрасту – корак по корак.',
  intro: 'Пети том садржи 40 вежби за координацију, брзину, снагу сопственом тежином и безбедан рад.',
  items: ['Загревање и истезање', 'Координација и агилност (лествице, чуњеви)', 'Брзина и реакција', 'Снага сопственом тежином', 'Скокови и доскоци', 'Вежбе за скочни зглоб и колено', 'Оптерећење по узрасту, хидратација и одмор', '40 вежби'],
  outro: 'Вежбе које помажу безбедан рад са децом.',
  note: 'Материјал не замењује савет лекара. Код повреде или здравствених тегоба играч се обраћа лекару.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 06',
  level: 'Напредак',
  title: 'Праћење напретка, тестови, родитељи и такмичења',
  tagline: 'Видите напредак – и покажите га играчима и родитељима.',
  intro: 'Шести том спаја тестове и листове за праћење са вођењем утакмице, турнирима и комуникацијом са родитељима.',
  items: ['Батерија тестова: техника и физички тестови', 'Табеле и листови за праћење у 3 нивоа', 'Лист индивидуалног напретка', 'Вођење утакмице: минутажа, тајм-аути, понашање', 'Организација турнира', 'Родитељски састанак и правила понашања на утакмици', 'Готове поруке за Viber групу', '100 готових коментара за повратну информацију играчима'],
  outro: 'Мање објашњавања – више јасних резултата.',
  note: 'Независан приручник за тренере. Није издање нити одобрење Кошаркашког савеза Србије.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 дигиталних томова за тренере кошарке',
  text: 'Комплетан систем који вам помаже да испланирате и водите тренинге током целе сезоне. Сваки том има 40 страна са готовим тренинзима, вежбама и играма у 3 нивоа и дијаграмима терена.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: TimerIcon,
  title: 'Готови тренинзи минут по минут',
  text: 'Крај празном папиру. Јасни блокови по минутима, материјал, организација и упутство за тренера.'
}, {
  icon: LayersIcon,
  title: 'Све у 3 нивоа',
  text: 'Иста вежба у три верзије – основни, средњи и напредни ниво – да цела група ради заједно.'
}, {
  icon: BookOpenTextIcon,
  title: 'Дијаграми терена',
  text: 'Свака вежба и игра има дијаграм: где стоје играчи, куда иду и где је лопта.'
}, {
  icon: CalendarCheckIcon,
  title: 'План целе сезоне',
  text: 'Сезона септембар–јун по узрасним групама, са периодима, месечним циљевима и микроциклусима.'
}, {
  icon: DumbbellIcon,
  title: 'Безбедан рад по узрасту',
  text: 'Загревање, координација, скокови и доскоци и оптерећење прилагођено деци.'
}, {
  icon: TrophyIcon,
  title: 'Утакмице, турнири и родитељи',
  text: 'Тестови, листови напретка, вођење утакмице, организација турнира и готове поруке за родитеље.'
}];
// Real pages from Том 1 (only reviewed OK pages). The «Погледајте унутра» section stays hidden while this list is empty.
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [];
export const pageTypes = ['Готови тренинзи', 'Вежбе у 3 нивоа', 'Дијаграми терена', 'Праћење напретка'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: '50 загревања за 10 минута',
  text: 'Загревања без припреме, распоређена по узрасту, да тренинг почне одмах и у покрету.',
  items: ['50 загревања', 'По 10 минута', 'По узрасту', 'Без припреме'],
  benefit: 'Сваки тренинг почиње брзо и енергично.'
}, {
  title: 'Пакет за замену – 12 тренинга без припреме',
  text: 'Када морате да оставите групу помоћнику или родитељу, тренинг и даље има јасан план.',
  items: ['12 готових тренинга', 'Једноставна упутства', 'За помоћника или родитеља', 'Спремно за штампу'],
  benefit: 'Ниједан изгубљен тренинг када сте одсутни.'
}, {
  title: 'Тренер и родитељи – готове поруке и обрасци',
  text: 'Поруке за Viber групу, сагласности, правила и материјал за родитељски састанак.',
  items: ['Поруке за Viber групу', 'Сагласности', 'Правила понашања', 'Родитељски састанак'],
  benefit: 'Јасна комуникација без свакодневног писања од нуле.'
}, {
  title: 'Постери и картице за салу',
  text: 'Правила игре, знакови судија, кључне речи технике и картице вежби за штампу.',
  items: ['Правила игре', 'Знакови судија', 'Кључне речи технике', 'Картице вежби'],
  benefit: 'Деца виде правила и кључне речи на зиду сале.'
}, {
  title: 'Камп и турнир – комплетан план',
  text: 'Летњи или зимски камп од 5 дана и школски или клупски турнир корак по корак.',
  items: ['Камп од 5 дана', 'Распоред по данима', 'Турнир корак по корак', 'Контролне листе'],
  benefit: 'Камп и турнир без хаоса у последњем тренутку.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Отворите пакет',
  text: 'Преузмите КОШАРКА СПРЕМНА™ и отворите том који вам треба.'
}, {
  title: 'Изаберите узраст и тренинг',
  text: 'Пронађите тренинг, вежбу или игру за своју групу и период сезоне.'
}, {
  title: 'Припремите се за 5 минута',
  text: 'Прочитајте упутство за тренера, погледајте дијаграм и спремите чуњеве и лопте.'
}, {
  title: 'Уђите у салу са планом',
  text: 'Одштампајте страну или је отворите на телефону – и почните тренинг.'
}];
export const audienceItems = ['Тренирате децу или млађе категорије (6–16 година)', 'Водите школу кошарке или мини-баскет', 'Радите са млађим пионирима, пионирима или кадетима', 'Наставник сте физичког васпитања и водите кошаркашку секцију', 'Родитељ сте који тренира екипу у малом клубу', 'Желите план целе сезоне по узрасту', 'Радите са групама различитог нивоа', 'Желите да вам вечери буду слободне'];
export const timeValueTasks = ['Тражење вежби и игара на интернету', 'Прављење плана сезоне', 'Цртање дијаграма за сваки тренинг', 'Прилагођавање вежби за различите нивое', 'Поруке родитељима и организација утакмица'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 дигиталних томова – 240 страна',
  detail: 'Годишњи план, готови тренинзи, техника у 3 нивоа, тактика и игра, физичка припрема и праћење напретка'
}, {
  label: '5 бонуса без доплате',
  detail: '50 загревања, Пакет за замену, Тренер и родитељи, Постери и картице за салу и Камп и турнир'
}, {
  label: 'За узраст 6–16 година',
  detail: '150+ вежби и игара са дијаграмима терена, у 3 нивоа'
}];
export const offerIncludes = ['Тренутни дигитални приступ', 'PDF фајлови које чувате', 'Спремно за штампу', 'Ништа се не шаље поштом'];
// Serbian dinar (RSD), Serbian notation: "2.490 дин." = Market «Србија».
export const PRICE = '2.490 дин.';
export const PRICE_BEFORE = '5.990 дин.';

// Only add authentic reviews of КОШАРКА СПРЕМНА. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Како добијам материјал?',
  a: 'КОШАРКА СПРЕМНА™ се испоручује дигитално. После куповине на мејл добијате приступ PDF фајловима које можете да преузмете и сачувате. Материјал користите на рачунару, таблету или телефону и штампате стране које су вам потребне.'
}, {
  q: 'За који узраст је материјал?',
  a: 'За децу и млађе категорије од 6 до 16 година, у четири оријентационе групе: школа кошарке / мини-баскет (6–10), млађи пионири (11–12), пионири (13–14) и кадети (15–16). Ви бирате тренинге и вежбе према својој групи.'
}, {
  q: 'Да ли је ово званични програм Кошаркашког савеза Србије?',
  a: 'Не. КОШАРКА СПРЕМНА™ је независан приручник за тренере. Није издање нити одобрење Кошаркашког савеза Србије. Узрасне групе су оријентационе, а правила такмичења увек проверите у свом савезу и лиги.'
}, {
  q: 'Да ли ми треба тренерска лиценца?',
  a: 'Материјал је направљен за тренере, наставнике физичког васпитања и родитеље-тренере. Не замењује тренерско образовање, али вам даје готов план, тренинге и вежбе за свакодневни рад.'
}, {
  q: 'Колико траје један тренинг?',
  a: 'Тренинзи су планирани за 60–90 минута (за децу 6–10 година 60 минута), за 2–4 тренинга недељно. Сваки тренинг има блокове по минутима које можете да скратите или продужите.'
}, {
  q: 'Шта ако у групи имам децу различитог нивоа?',
  a: 'Вежбе у Тому 3 постоје у 3 нивоа – основни, средњи и напредни – а тренинзи у Тому 2 имају варијанте за мању и већу групу. Тако цела група ради исту тему, свако на свом нивоу.'
}, {
  q: 'Који реквизити су ми потребни?',
  a: 'Углавном оно што већ имате у сали: лопте, чуњеви, маркери и кош. Уз сваки тренинг пише тачан материјал. За неке вежбе координације добро дође лествица, али свака има и варијанту без ње.'
}, {
  q: 'Да ли материјал помаже у спречавању повреда?',
  a: 'Том 5 садржи загревање, истезање, вежбе за скочни зглоб и колено и оптерећење по узрасту – вежбе које помажу безбедан рад. Материјал не замењује савет лекара: код повреде или тегоба играч се обраћа лекару.'
}, {
  q: 'Има ли материјала за рад са родитељима?',
  a: 'Да. Том 6 и бонус „Тренер и родитељи“ садрже родитељски састанак, правила понашања на утакмици, сагласности и готове поруке за Viber групу.'
}, {
  q: 'Да ли морам све да одштампам?',
  a: 'Не. Сами бирате које стране штампате. Многи тренери користе тренинг на телефону у сали, а постере и картице одштампају једном.'
}, {
  q: 'Да ли је пет бонуса укључено у цену?',
  a: 'Да. Свих пет бонуса је у пакету без доплате.'
}, {
  q: 'Да ли је ово претплата?',
  a: 'Не. Плаћате једном и добијате приступ дигиталном материјалу. Без месечних плаћања.'
}, {
  q: 'Постоји ли гаранција?',
  a: 'Да. КОШАРКА СПРЕМНА™ има гаранцију задовољства од 7 дана. Ако противно очекивањима нисте задовољни материјалом, можете да нам пишете и да добијете повраћај новца у гарантном року, према условима гаранције.'
}];
